import { HttpClient } from '@angular/common/http';
import { Pipe, PipeTransform } from '@angular/core';
import { Observable, of, switchMap, zip } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { RenderSlugService } from 'render/services/render-slug.service';
import { RenderStateService } from 'render/services/render-state/render-state.service';


interface MetaJSON {
  publicMetadata?: {
    altTexts: Record<string, string>;
  };
}

@Pipe({
  name: 'getAltText',
  standalone: true
})
export class AltTextPipe implements PipeTransform {

  constructor(
    private readonly http: HttpClient,
    private readonly renderSlugService: RenderSlugService,
    private readonly renderStateService: RenderStateService
  ) { }

  public transform(url: string, defaultAlt?: string): Observable<string> {
    if (defaultAlt) {
      return of(defaultAlt);
    }

    // Read from the beginning up to exactly four slash-ended segments after assets/public/
    const pathRegex = /^(.*?assets\/public\/(?:[^/]+\/){4})/;
    const match = pathRegex.exec(url);
    if (!match) {
      // It is not an Amanda URL
      return of('');
    }

    return this.http.get<MetaJSON>(`${match[1]}meta.json`).pipe(
      switchMap(this.formatHttpResponse.bind(this)),
      // We don't bother handling eventual HTTP errors, just return an empty string
      catchError(() => of(''))
    );
  }

  private formatHttpResponse(response: MetaJSON): Observable<string> {
    if (!response.publicMetadata) {
      return of('');
    }

    const choices = response.publicMetadata.altTexts;
    const slug = this.renderSlugService.getSlug();
    const country$ = this.renderStateService.getCountry$(slug);
    const lang$ = this.renderStateService.getLanguage$(slug);

    return zip([country$, lang$]).pipe(
      map(([country, lang]) => {
        let result;
        if (country && lang) {
          if (choices[`${lang}-${country}`]) {
            result = choices[`${lang}-${country}`];
          } else {
            result = getByLanguage(lang, choices);

            if (!result) {
              result = getByCountry(country, choices);
            }
          }
        } else if (!country && lang) {
          result = getByLanguage(lang, choices);
        } else if (country && !lang) {
          result = getByCountry(country, choices);
        }

        return result ?? choices['en-GB'] ?? '';
      })
    );
  }

}

function getByLanguage(lang: string, choices: Record<string, string>): string | undefined {
  const byLanguage = Object.keys(choices).find((key) => key.startsWith(lang));
  return byLanguage ? choices[byLanguage] : undefined;
}

function getByCountry(country: string, choices: Record<string, string>): string | undefined {
  const byCountry = Object.keys(choices).find((key) => key.endsWith(country));
  return byCountry ? choices[byCountry] : undefined;
}
